import { useState } from 'react';
import { Phone, Mail, MapPin, ArrowRight } from 'lucide-react';

export default function ContactSection() {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    projectType: '',
    message: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.firstName || !formData.lastName || !formData.email || !formData.phone) {
      setStatus({ type: 'error', message: 'Please fill in all required fields.' });
      return;
    }

    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // EmailJS configuration
      const service_id = "service_crkbcn9";
      const template_id = "template_7niemfs";
      const publicKey = 'user_5rV9mbyE2QLYgUUUhwhd2';


      const templateParams = {
        from_name: `${formData.firstName} ${formData.lastName}`,
        from_email: formData.email,
        phone: formData.phone,
        project_type: formData.projectType || 'Not specified',
        message: formData.message || 'No additional details provided'
      };

      const response = await fetch("https://api.emailjs.com/api/v1.0/email/send", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          service_id: service_id,
          template_id: template_id,
          user_id: publicKey,
          template_params: templateParams
        })
      });

      if (response.ok) {
        setStatus({ 
          type: 'success', 
          message: 'Thank you! We\'ll be in touch within 24 hours.' 
        });
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          projectType: '',
          message: ''
        });
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      setStatus({ 
        type: 'error', 
        message: 'Something went wrong. Please call us at (435) 414-0098.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'black' , padding: '4rem 1rem' }}>
      <section id="contact" style={{ position: 'relative', padding: '4rem 0' }}>
        <div style={{
          position: 'absolute',
          inset: 0,
          background: 'radial-gradient(circle at 30% 50%, rgba(239, 68, 68, 0.15), transparent 50%), radial-gradient(circle at 70% 50%, rgba(234, 179, 8, 0.15), transparent 50%)',
          pointerEvents: 'none'
        }}></div>
        
        <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative', zIndex: 10 }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <h2 style={{
              fontSize: 'clamp(2rem, 5vw, 3.5rem)',
              fontWeight: 800,
              background: 'linear-gradient(to right, #ef4444, #f97316, #eab308)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              marginBottom: '1rem'
            }}>
              Ready to Begin?
            </h2>
            <p style={{ fontSize: '1.25rem', color: '#9ca3af' }}>
              Let's create something extraordinary together
            </p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '3rem',
            alignItems: 'start'
          }}>
            <div>
              <h3 style={{
                fontSize: '2rem',
                fontWeight: 700,
                color: '#ffffff',
                marginBottom: '1rem'
              }}>
                Get Your Vision Started
              </h3>
              <p style={{
                color: '#9ca3af',
                fontSize: '1.125rem',
                lineHeight: 1.7,
                marginBottom: '2rem'
              }}>
                Every masterpiece begins with a conversation. We'll visit your space, understand your vision, and engineer the perfect solution.
              </p>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                {[
                  {
                    icon: <Phone style={{ width: '1.5rem', height: '1.5rem' }} />,
                    title: "Call or Text",
                    content: "(435) 414-0098",
                    gradient: "linear-gradient(135deg, #ef4444, #f97316)"
                  },
                  {
                    icon: <Mail style={{ width: '1.5rem', height: '1.5rem' }} />,
                    title: "Email",
                    content: "Sales@SlickRockPainting.com",
                    gradient: "linear-gradient(135deg, #f97316, #eab308)"
                  },
                  {
                    icon: <MapPin style={{ width: '1.5rem', height: '1.5rem' }} />,
                    title: "Service Area",
                    content: "All of Southern Utah",
                    gradient: "linear-gradient(135deg, #eab308, #ef4444)"
                  }
                ].map((contact, index) => (
                  <div key={index} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <div style={{
                      width: '3.5rem',
                      height: '3.5rem',
                      borderRadius: '1rem',
                      background: contact.gradient,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#ffffff',
                      flexShrink: 0
                    }}>
                      {contact.icon}
                    </div>
                    <div>
                      <p style={{ color: '#9ca3af', fontSize: '0.875rem', fontWeight: 500 }}>
                        {contact.title}
                      </p>
                      <p style={{ fontSize: '1.25rem', fontWeight: 700, color: '#ffffff' }}>
                        {contact.content}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            
            <div style={{
              background: 'rgba(31, 41, 55, 0.5)',
              backdropFilter: 'blur(10px)',
              borderRadius: '1.5rem',
              padding: '2.5rem',
              border: '1px solid rgba(255, 255, 255, 0.1)'
            }}>
              <h3 style={{
                fontSize: '1.875rem',
                fontWeight: 700,
                color: '#ffffff',
                marginBottom: '2rem'
              }}>
                Start Your Project
              </h3>
              
              {status.message && (
                <div style={{
                  padding: '1rem',
                  borderRadius: '0.5rem',
                  marginBottom: '1.5rem',
                  backgroundColor: status.type === 'success' ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)',
                  border: `1px solid ${status.type === 'success' ? '#22c55e' : '#ef4444'}`,
                  color: status.type === 'success' ? '#22c55e' : '#ef4444'
                }}>
                  {status.message}
                </div>
              )}
              
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem' }}>
                  <input
                    type="text"
                    name="firstName"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    style={{
                      padding: '0.875rem 1rem',
                      backgroundColor: '#1f2937',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '0.75rem',
                      color: '#ffffff',
                      fontSize: '1rem',
                      outline: 'none',
                      transition: 'all 0.2s'
                    }}
                  />
                  <input
                    type="text"
                    name="lastName"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    style={{
                      padding: '0.875rem 1rem',
                      backgroundColor: '#1f2937',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '0.75rem',
                      color: '#ffffff',
                      fontSize: '1rem',
                      outline: 'none'
                    }}
                  />
                </div>
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  style={{
                    padding: '0.875rem 1rem',
                    backgroundColor: '#1f2937',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '0.75rem',
                    color: '#ffffff',
                    fontSize: '1rem',
                    outline: 'none'
                    
                  }}
                />
                <input
                  type="tel"
                  name="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  style={{
                    padding: '0.875rem 1rem',
                    backgroundColor: '#1f2937',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '0.75rem',
                    color: '#ffffff',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
                <select
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleInputChange}
                  style={{
                    padding: '0.875rem 1rem',
                    backgroundColor: '#1f2937',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '0.75rem',
                    color: formData.projectType ? '#ffffff' : '#9ca3af',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                >
                  <option value="">Select Your Project Type</option>
                  <option value="exterior">Exterior Painting</option>
                  <option value="interior">Interior Painting</option>
                  <option value="both">Complete Home Transformation</option>
                  <option value="commercial">Commercial Project</option>
                  <option value="other">Something Else</option>
                </select>
                <textarea
                  name="message"
                  placeholder="Tell us about your vision..."
                  rows={4}
                  value={formData.message}
                  onChange={handleInputChange}
                  style={{
                    padding: '0.875rem 1rem',
                    backgroundColor: '#1f2937',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '0.75rem',
                    color: '#ffffff',
                    fontSize: '1rem',
                    outline: 'none',
                    resize: 'vertical'
                  }}
                ></textarea>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  style={{
                    padding: '1rem 2rem',
                    background: 'linear-gradient(135deg, #ef4444, #f97316)',
                    border: 'none',
                    borderRadius: '0.75rem',
                    color: '#ffffff',
                    fontSize: '1.125rem',
                    fontWeight: 600,
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.5rem',
                    opacity: isSubmitting ? 0.7 : 1,
                    transition: 'all 0.2s'
                  }}
                >
                  <span>{isSubmitting ? 'Sending...' : 'Get Your Free Consultation'}</span>
                  {!isSubmitting && <ArrowRight style={{ width: '1.25rem', height: '1.25rem' }} />}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}